'use client';

import { useMemo } from 'react';
import { motion } from 'framer-motion';
import { Briefcase, GraduationCap, Calendar, MapPin } from 'lucide-react';
import { useSiteData } from '@/lib/dataCache';

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: 'easeOut' as const } },
};

type ApiTimeline = {
  id: string;
  type: string; // "work" | "education"
  title: string;
  company: string;
  period: string;
  description: string;
  techStack: string[];
  logoUrl?: string | null;
};

function TimelineBlock({
  items,
  variant,
}: {
  items: ApiTimeline[];
  variant: 'work' | 'edu';
}) {
  if (items.length === 0) {
    return (
      <div className="timeline-list">
        <div className="timeline-empty">
          {variant === 'work'
            ? 'No work experience added yet.'
            : 'No education added yet.'}
        </div>
      </div>
    );
  }

  return (
    <div className="timeline-list">
      <div className="timeline-rail" />
      {items.map((item, i) => (
        <motion.div
          key={item.id}
          className="timeline-item"
          initial={{ opacity: 0, x: 30 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true, margin: '-60px' }}
          transition={{ duration: 0.6, delay: i * 0.1, ease: 'easeOut' }}
        >
          <div className="timeline-dot">
            {variant === 'work' ? (
              <Briefcase size={14} />
            ) : (
              <GraduationCap size={14} />
            )}
          </div>

          <div className="timeline-card">
            <div className="timeline-card-head">
              <span className="timeline-period">
                <Calendar size={12} />
                {item.period}
              </span>
            </div>

            <h4 className="timeline-title">{item.title}</h4>
            <div className="timeline-company">
              <MapPin size={13} />
              <span>{item.company}</span>
            </div>

            <p className="timeline-desc">{item.description}</p>

            {item.techStack?.length > 0 && (
              <div className="timeline-tags">
                {item.techStack.map((tech) => (
                  <span key={tech} className="timeline-tag">
                    {tech}
                  </span>
                ))}
              </div>
            )}
          </div>
        </motion.div>
      ))}
    </div>
  );
}

export default function Timeline() {
  const entries = useSiteData<ApiTimeline[]>('timeline', '/api/timeline', []);

  const { workItems, eduItems } = useMemo(() => {
    const work: ApiTimeline[] = [];
    const edu: ApiTimeline[] = [];
    for (const e of entries) {
      const t = (e.type || '').toLowerCase();
      if (t === 'education' || t === 'edu') {
        edu.push(e);
      } else {
        work.push(e);
      }
    }
    return { workItems: work, eduItems: edu };
  }, [entries]);

  return (
    <section id="timeline" className="timeline-section">
      <div className="timeline-blob timeline-blob-1" />
      <div className="timeline-blob timeline-blob-2" />

      <div className="section-container">
        <motion.div
          className="section-heading"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-80px' }}
          variants={fadeUp}
        >
          <span className="section-label">My Journey</span>
          <h2 className="section-title">Experience &amp; Education</h2>
          <p className="section-subtitle">
            A snapshot of the roles, companies and studies that shaped my path
            as a developer.
          </p>
        </motion.div>

        <div className="timeline-wrapper">
          {/* Work Experience */}
          <motion.div
            className="timeline-group"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <div className="timeline-group-head">
              <div className="timeline-group-icon timeline-group-icon-work">
                <Briefcase size={20} />
              </div>
              <div>
                <h3 className="timeline-group-title">Work Experience</h3>
                <p className="timeline-group-sub">
                  Roles that honed my craft
                </p>
              </div>
            </div>

            <TimelineBlock items={workItems} variant="work" />
          </motion.div>

          {/* Education */}
          <motion.div
            className="timeline-group"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            <div className="timeline-group-head">
              <div className="timeline-group-icon timeline-group-icon-edu">
                <GraduationCap size={20} />
              </div>
              <div>
                <h3 className="timeline-group-title">Education</h3>
                <p className="timeline-group-sub">
                  Foundations of my knowledge
                </p>
              </div>
            </div>

            <TimelineBlock items={eduItems} variant="edu" />
          </motion.div>
        </div>
      </div>
    </section>
  );
}
